import type { ReactNode } from "react";
import {
    Navigate,
    Outlet,
    Route,
    Routes,
    useLocation,
    useSearchParams,
} from "react-router-dom";

import { routes, strings } from "@/constants";
import { RoleEnum } from "@/enums";
import { useAuth } from "@/hooks";
import { ProfileLayout } from "@/layouts";
import {
    AdminCompanies,
    AdminCompanyDetail,
    AdminInvitations,
    AdminSendInvite,
    Benchmark,
    Benchmarks,
    ForgotPassword,
    Login,
    NotFound,
    Profile,
    Register,
    ResetPassword,
    Result,
} from "@/pages";
import { isAuthenticatedHandler } from "@/utils";

const ProtectedRoute = ({ children }: { children: ReactNode }) => {
    const location = useLocation();

    if (!isAuthenticatedHandler()) {
        const returnUrl = location.pathname + location.search;

        return (
            <Navigate
                to={`${routes.login}?${strings.queryParams.returnUrl}=${encodeURIComponent(returnUrl)}`}
                replace
            />
        );
    }

    return <>{children}</>;
};

const ProfileShellLayout = () => {
    const location = useLocation();

    if (!isAuthenticatedHandler()) {
        const returnUrl = location.pathname + location.search;

        return (
            <Navigate
                to={`${routes.login}?${strings.queryParams.returnUrl}=${encodeURIComponent(returnUrl)}`}
                replace
            />
        );
    }

    return (
        <ProfileLayout>
            <Outlet />
        </ProfileLayout>
    );
};

const AdminGuardLayout = () => {
    const { user } = useAuth();

    if (user?.role !== RoleEnum.ADMIN) {
        return (
            <Navigate
                to={routes.profile}
                replace
            />
        );
    }

    return <Outlet />;
};

const PublicRoute = ({ children }: { children: ReactNode }) => {
    const [searchParams] = useSearchParams();

    if (isAuthenticatedHandler()) {
        const returnUrl = searchParams.get(strings.queryParams.returnUrl);

        return (
            <Navigate
                to={returnUrl || routes.benchmark}
                replace
            />
        );
    }

    return <>{children}</>;
};

export const AppRoutes = () => (
    <Routes>
        <Route
            path={routes.login}
            element={(
                <PublicRoute>
                    <Login />
                </PublicRoute>
            )}
        />
        <Route
            path={routes.forgotPassword}
            element={(
                <PublicRoute>
                    <ForgotPassword />
                </PublicRoute>
            )}
        />
        <Route
            path={routes.resetPassword}
            element={(
                <PublicRoute>
                    <ResetPassword />
                </PublicRoute>
            )}
        />
        <Route
            path={routes.register}
            element={(
                <PublicRoute>
                    <Register />
                </PublicRoute>
            )}
        />
        <Route
            path={routes.benchmark}
            element={(
                <ProtectedRoute>
                    <Benchmark />
                </ProtectedRoute>
            )}
        />
        <Route
            path={routes.result}
            element={(
                <ProtectedRoute>
                    <Result />
                </ProtectedRoute>
            )}
        />
        <Route element={<ProfileShellLayout />}>
            <Route
                element={<Profile />}
                path={routes.profile}
            />
            <Route
                element={<Benchmarks />}
                path={routes.benchmarks}
            />
            <Route element={<AdminGuardLayout />}>
                <Route
                    element={<AdminCompanies />}
                    path={routes.adminCompanies}
                />
                <Route
                    element={<AdminCompanyDetail />}
                    path={routes.adminCompanyDetail}
                />
                <Route
                    element={<AdminInvitations />}
                    path={routes.adminInvitations}
                />
                <Route
                    element={<AdminSendInvite />}
                    path={routes.adminSendInvite}
                />
            </Route>
        </Route>
        <Route
            path={routes.root}
            element={(
                <Navigate
                    to={routes.login}
                    replace
                />
            )}
        />
        <Route
            element={<NotFound />}
            path="*"
        />
    </Routes>
);
